import { getErrorStatus } from '@/api/errors'
import {
  ChainLinkCard,
  ChainLinkDetailPanel,
  ChainPauseBanner,
  ChainPauseUnresolvedBanner,
  ChainStoppedBanner,
  chainRunProgressColorClasses,
  ResumeChainDialog,
} from '@/components/projects/chains/runs'
import { AutomaticStartBadge } from '@/components/projects/chains/runs/AutomaticStartBadge'
import { LinkedEntityBadge, WorkflowStatusBadge } from '@/components/projects/workflows/shared'
import { Badge, Button, Progress } from '@/components/ui'
import { useAuth, useChainRun, useNowTick, useProject } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { formatDurationMs, formatElapsedTime, formatTimestampShort } from '@/lib/timeFormatters'
import { cn } from '@/lib/utils'
import type { ChainRunDetail, ChainRunLink } from '@/types'
import { isActiveChainRunStatus } from '@/types'
import { AlertCircle, ArrowLeft, Link2, Loader2, Play } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

const RESUME_BLOCKED_REASON_ID = 'chain-resume-blocked-reason'

function findWaitingLink(chainRun: ChainRunDetail, links: ChainRunLink[]): ChainRunLink | null {
  if (chainRun.status !== 'AwaitingInputs' && chainRun.status !== 'AwaitingManualStart') return null

  return links.find(l => l.linkName === chainRun.currentLinkName)
    ?? links.find(l => l.status === 'Pending')
    ?? null
}

function findDefaultLink(links: ChainRunLink[], waitingLink: ChainRunLink | null): ChainRunLink | null {
  if (waitingLink) return waitingLink

  return links.find(l => l.status === 'Triggered')
    ?? [...links].reverse().find(l => l.status !== 'Pending')
    ?? null
}

export function ChainRunDetailPage() {
  const { projectId, chainRunId } = useParams()
  const navigate = useNavigate()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null)
  const [isPanelClosed, setIsPanelClosed] = useState(false)
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false)

  const { data: chainRun, isLoading, isError, error, refetch } = useChainRun(projectId!, chainRunId)
  const { data: project } = useProject(projectId)

  const links = useMemo(
    () => [...(chainRun?.links ?? [])].sort((a, b) => a.position - b.position),
    [chainRun?.links],
  )

  const waitingLink = chainRun ? findWaitingLink(chainRun, links) : null
  const defaultLink = findDefaultLink(links, waitingLink)
  const selectedLink = isPanelClosed
    ? null
    : links.find(l => l.id === selectedLinkId) ?? defaultLink

  const isChainActive = chainRun ? isActiveChainRunStatus(chainRun.status) : false
  useNowTick(isChainActive)

  if (authLoading) {
    return null
  }

  if (!hasPermission(Permissions.Project.Chain.View, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite" data-testid="chain-run-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading chain run details" />
      </div>
    )
  }

  if (isError && getErrorStatus(error) !== 404) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16" role="alert" data-testid="chain-run-detail-error">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <h3 className="mt-4 text-lg font-semibold">Failed to load chain run</h3>
          <p className="mt-1 text-sm text-muted-foreground">Something went wrong while loading this chain run.</p>
          <Button onClick={() => refetch()} variant="outline" className="mt-4">
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  if (isError || !chainRun) {
    return (
      <div className="p-6" data-testid="chain-run-detail-not-found">
        <p className="text-muted-foreground">Chain run not found.</p>
        <Button variant="ghost" className="mt-2" onClick={() => navigate(`/projects/${projectId}/workflows/runs?tab=chains`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </div>
    )
  }

  const isPaused = chainRun.status === 'AwaitingInputs' || chainRun.status === 'AwaitingManualStart'
  const canResume = hasPermission(Permissions.Project.Chain.Start, projectId)
  const permissionBlockedReason = canResume ? undefined : 'You need the Start chain permission to resume this chain.'
  const resumeBlockedReason = permissionBlockedReason
    ?? (waitingLink ? undefined : 'The link this chain waits on could not be resolved.')
  const resumeMode = chainRun.status === 'AwaitingManualStart' ? 'manual' : 'inputs'
  const waitingText = chainRun.status === 'AwaitingManualStart'
    ? 'Waiting for a manual start'
    : 'Waiting for the missing inputs'

  const linkProgress = chainRun.totalLinks > 0 ? (chainRun.completedLinks / chainRun.totalLinks) * 100 : 0
  const duration = chainRun.completedAt == null && chainRun.startedAt
    ? formatElapsedTime(chainRun.startedAt, chainRun.completedAt)
    : formatDurationMs(chainRun.durationMs)

  const handleSelectLink = (linkId: string) => {
    setSelectedLinkId(linkId)
    setIsPanelClosed(false)
  }

  const openRun = (workflowRunId: string) => navigate(`/projects/${projectId}/workflows/runs/${workflowRunId}`)
  const openChat = (chatId: string) => navigate(`/projects/${projectId}/chat?chatId=${chatId}`)

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <div className="p-4 border-b shrink-0">
        {project && (
          <div className="text-xs text-muted-foreground mb-2">
            {project.name}
          </div>
        )}

        <div className="flex items-center gap-3 mb-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate(`/projects/${projectId}/workflows/runs?tab=chains`)}
            aria-label="Back to runs"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground shrink-0">
            <Link2 className="h-4 w-4" />
          </div>
          <span className="font-semibold text-lg">{chainRun.chainDisplayName}</span>
          <Badge variant="secondary" className="text-xs">v{chainRun.definitionVersion}</Badge>
          <WorkflowStatusBadge status={chainRun.status} />
          <div className="flex items-center gap-2 w-32">
            <Progress value={linkProgress} className="h-2 flex-1" indicatorClassName={chainRunProgressColorClasses[chainRun.status]} />
            <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
              {chainRun.completedLinks}/{chainRun.totalLinks}
            </span>
          </div>
          <LinkedEntityBadge type={chainRun.linkedEntityType} number={chainRun.linkedEntityNumber} fallbackId={chainRun.linkedEntityId} entityId={chainRun.linkedEntityId} available={chainRun.linkedEntityAvailable} status={chainRun.linkedEntityStatus} />
          {isPaused && (
            <div className="ml-auto flex items-center gap-2">
              {resumeBlockedReason && (
                <span id={RESUME_BLOCKED_REASON_ID} className="text-xs text-warning-foreground">
                  {resumeBlockedReason}
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                className={cn('bg-toolbar border-border-accent', resumeBlockedReason && 'opacity-50 cursor-not-allowed')}
                aria-disabled={!!resumeBlockedReason}
                aria-describedby={resumeBlockedReason ? RESUME_BLOCKED_REASON_ID : undefined}
                aria-label="Resume chain"
                onClick={() => { if (!resumeBlockedReason) setResumeDialogOpen(true) }}
              >
                <Play className="h-3 w-3 mr-1" /> Resume
              </Button>
            </div>
          )}
        </div>

        <div className="rounded-lg border bg-card px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-y-2 text-sm text-muted-foreground">
            <span><span className="font-medium text-foreground"># Chain-{chainRun.id.slice(0, 8)}</span></span>
            <span>Created: <span className="font-medium text-foreground">{formatTimestampShort(chainRun.createdAt)}</span></span>
            <span>Started: <span className="font-medium text-foreground">{formatTimestampShort(chainRun.startedAt)}</span></span>
            <span>Ended: <span className="font-medium text-foreground">{formatTimestampShort(chainRun.completedAt)}</span></span>
            <span>Duration: <span className="font-medium text-foreground">{duration}</span></span>
            <span className="inline-flex items-center gap-1.5">
              User: <span className="font-medium text-foreground">{chainRun.triggeredBy}</span>
              <AutomaticStartBadge triggerSource={chainRun.triggerSource} />
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 p-4 gap-4">
        <div className="w-[460px] shrink-0 flex flex-col rounded-lg border bg-card">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-4 pt-4 pb-2">
            Links
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-3">
            {(chainRun.status === 'Failed' || chainRun.status === 'Cancelled') && (
              <ChainStoppedBanner status={chainRun.status} errorSummary={chainRun.errorSummary} />
            )}
            {isPaused && !waitingLink && (
              <ChainPauseUnresolvedBanner
                mode={resumeMode}
                errorSummary={chainRun.errorSummary}
                blockedReason={permissionBlockedReason}
              />
            )}
            {links.map(link => (
              <div key={link.id} className="space-y-3">
                {waitingLink?.id === link.id && (
                  <ChainPauseBanner
                    link={link}
                    mode={resumeMode}
                    errorSummary={chainRun.errorSummary}
                    blockedReason={resumeBlockedReason}
                    onResume={() => setResumeDialogOpen(true)}
                  />
                )}
                <ChainLinkCard
                  link={link}
                  isExpanded={selectedLink?.id === link.id || link.status === 'Triggered'}
                  isWaiting={waitingLink?.id === link.id}
                  waitingText={waitingLink?.id === link.id ? waitingText : null}
                  onSelect={() => handleSelectLink(link.id)}
                  onOpenRun={openRun}
                />
              </div>
            ))}
          </div>
        </div>

        {selectedLink && (
          <div className="flex-1 min-w-0 flex flex-col">
            <ChainLinkDetailPanel
              link={selectedLink}
              projectId={projectId!}
              isWaiting={waitingLink?.id === selectedLink.id}
              onClose={() => setIsPanelClosed(true)}
              onOpenRun={openRun}
              onOpenChat={openChat}
            />
          </div>
        )}
      </div>

      <ResumeChainDialog
        projectId={projectId!}
        chainRunId={chainRun.id}
        link={waitingLink}
        mode={resumeMode}
        open={resumeDialogOpen}
        onOpenChange={setResumeDialogOpen}
      />
    </div>
  )
}
